import { taskTypeApi, taskTypeExclusionApi } from 'app/services/api';
import { normalize, schema } from 'normalizr';

import { catchAsyncActionError } from 'utils/action';
import { getDefaultErrorMessage } from 'utils/axios';
import { logger } from 'utils/logger';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { selectMasterCompanyId } from 'store/auth/selectors';
import { showErrorMessageAction } from 'store/global/actions';

export const TASK_TYPES_ACTION_TYPE = createNamedActions('taskTypes', {
  GET_TASK_TYPES: 'GET_TASK_TYPES',
  EDIT_TASK_TYPE: 'EDIT_TASK_TYPE',
  DELETE_TASK_TYPE: 'DELETE_TASK_TYPE',
  ADD_TASK_TYPE: 'ADD_TASK_TYPE',
  CHANGE_TASK_TYPE_ACTIVITY: 'CHANGE_TASK_TYPE_ACTIVITY',
});

const taskTypesSchema = new schema.Entity('data', {}, { idAttribute: 'taskTypeId' });

export const getTaskTypesAction = catchAsyncActionError(() => async (dispatch, getState) => {
  const { data } = await taskTypeApi.getTypes({ companyId: selectMasterCompanyId(getState()) });

  return dispatch({
    type: TASK_TYPES_ACTION_TYPE.GET_TASK_TYPES,
    payload: normalize(data, [taskTypesSchema]).entities.data,
  });
});

export const getTaskTypesWithExclusionAction = catchAsyncActionError(
  () => async (dispatch, getState) => {
    const companyId = selectMasterCompanyId(getState());

    return Promise.all([
      taskTypeApi.getTypes({ companyId }),
      taskTypeExclusionApi.getTypes({ companyId }),
    ]).then(([taskTypes, excludedTaskTypeIds]) => {
      const normalizedTaskTypes = normalize(taskTypes.data, [taskTypesSchema]).entities.data;

      excludedTaskTypeIds.data.forEach(taskTypeId => {
        if (normalizedTaskTypes[taskTypeId]) {
          normalizedTaskTypes[taskTypeId].isExcluded = true;
        }
      });

      dispatch({
        type: TASK_TYPES_ACTION_TYPE.GET_TASK_TYPES,
        payload: normalizedTaskTypes,
      });
    });
  },
);

export const deleteTaskTypeAction = catchAsyncActionError(
  ({ taskTypeId }) =>
    async (dispatch, getState) => {
      await taskTypeApi.deleteType({ companyId: selectMasterCompanyId(getState()), taskTypeId });

      return dispatch({
        type: TASK_TYPES_ACTION_TYPE.DELETE_TASK_TYPE,
        payload: taskTypeId,
      });
    },
);

export const editTaskTypeAction = dataBeforeSave => async (dispatch, getState) => {
  // For D&D case
  if (dataBeforeSave.prevTaskCategoryId) {
    dispatch({
      type: TASK_TYPES_ACTION_TYPE.EDIT_TASK_TYPE,
      payload: dataBeforeSave,
    });
  }

  const { data } = await taskTypeApi
    .editType({ ...dataBeforeSave, companyId: selectMasterCompanyId(getState()) })
    .catch(err => {
      if (dataBeforeSave.prevTaskCategoryId) {
        dispatch({
          type: TASK_TYPES_ACTION_TYPE.EDIT_TASK_TYPE,
          payload: { ...dataBeforeSave, taskCategoryId: dataBeforeSave.prevTaskCategoryId },
        });
      }

      dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));
      logger.error(err);
    });

  return dispatch({
    type: TASK_TYPES_ACTION_TYPE.EDIT_TASK_TYPE,
    payload: data,
  });
};

export const addTaskTypeAction = catchAsyncActionError(params => async (dispatch, getState) => {
  const { data } = await taskTypeApi.createType({
    ...params,
    companyId: selectMasterCompanyId(getState()),
  });

  return dispatch({
    type: TASK_TYPES_ACTION_TYPE.ADD_TASK_TYPE,
    payload: data,
  });
});

export const createTaskTypeExclusionAction = catchAsyncActionError(
  ({ taskTypeId }) =>
    async (dispatch, getState) => {
      await taskTypeExclusionApi.createType({
        companyId: selectMasterCompanyId(getState()),
        taskTypeId,
      });

      return dispatch({
        type: TASK_TYPES_ACTION_TYPE.CHANGE_TASK_TYPE_ACTIVITY,
        payload: taskTypeId,
      });
    },
);

export const deleteTaskTypeExclusionAction = catchAsyncActionError(
  ({ taskTypeId }) =>
    async (dispatch, getState) => {
      await taskTypeExclusionApi.deleteType({
        companyId: selectMasterCompanyId(getState()),
        taskTypeId,
      });

      return dispatch({
        type: TASK_TYPES_ACTION_TYPE.CHANGE_TASK_TYPE_ACTIVITY,
        payload: taskTypeId,
      });
    },
);
